import { useRef, useState, useEffect } from "react";
import {
  extractBunnyThumbnail,
  extractYouTubeVideoId,
} from "../../utility/Common";
import BunnyVideo from "../BunneyVideo";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

const VideoCard = ({ video }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const ytId = extractYouTubeVideoId(video.URL);
  const thumbnail = ytId
    ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
    : extractBunnyThumbnail(video.URL);

  const modalPlayerRef = useRef(null);
  const modalPlyrInstance = useRef(null);

  useEffect(() => {
    if (!selectedVideo) return;
    const ytId = extractYouTubeVideoId(selectedVideo.URL);
    if (ytId && modalPlayerRef.current) {
      modalPlyrInstance.current = new Plyr(modalPlayerRef.current, {
        title: "",
        controls: [
          "play-large",
          "play",
          "progress",
          "current-time",
          "mute",
          "volume",
          "fullscreen",
        ],
        youtube: { noCookie: true, rel: 0, showinfo: 0 },
      });
    }
    return () => {
      modalPlyrInstance.current?.destroy();
      modalPlyrInstance.current = null;
    };
  }, [selectedVideo]);

  return (
    <>
      <button
        key={video.Id}
        type="button"
        onClick={() => setSelectedVideo(video)}
        className="group flex flex-col rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow text-left w-full"
      >
        <div className="relative w-full aspect-video bg-gray-900 flex items-center justify-center overflow-hidden">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={video.Title}
              className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
            />
          ) : (
            <svg
              className="w-12 h-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M14.752 11.168l-4.586-2.651A1 1 0 009 9.382v5.236a1 1 0 001.166.979l4.586-2.651a1 1 0 000-1.778z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          )}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
            <div className="bg-white/90 rounded-full p-3">
              <svg
                className="w-6 h-6 text-primary-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="p-3 flex flex-col gap-1">
          <p className="text-sm font-semibold text-gray-900 line-clamp-2">
            {video.Title}
          </p>
          {video.Description && (
            <p className="text-xs text-gray-500 line-clamp-2">
              {video.Description}
            </p>
          )}
        </div>
      </button>
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                {selectedVideo.Title}
              </p>
              <button
                type="button"
                onClick={() => setSelectedVideo(null)}
                className="text-gray-400 hover:text-gray-700 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {/* Player */}
            <div className="w-full bg-black">
              {extractYouTubeVideoId(selectedVideo.URL) ? (
                <>
                  <style>{`.plyr__title { display: none !important; }`}</style>
                  <div
                    ref={modalPlayerRef}
                    data-plyr-provider="youtube"
                    data-plyr-embed-id={extractYouTubeVideoId(
                      selectedVideo.URL,
                    )}
                  />
                </>
              ) : (
                <BunnyVideo videoUrl={selectedVideo.URL} />
              )}
            </div>
            {selectedVideo.Description && (
              <div className="px-5 py-3 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  {selectedVideo.Description}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoCard;
